import React, { useState } from "react";
import axios from "../services/api";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { Mail, MessageSquare, Tag, LogIn, Home } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from '../components/LanguageSwitcher';
import posthog from 'posthog-js';
import ReactGA from "react-ga4"; 
import { Helmet } from "react-helmet-async";
import i18n from '../i18n'; 


export default function ContactUsPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    ReactGA.event({
      category: "Button",
      action: "Click",
      label: "ContactUs Submitted",
    });

    if (!form.email || !form.subject || !form.message) {
      toast.error(t("contactErrorFields"));
      return;
    }

    setLoading(true);
    try {
      await axios.post("/demo-requests/", form);
      toast.success(t("contactSuccess"));
      posthog.capture('Contact Request Sent', {
        email: form.email,
        subject: form.subject,
      });
      setForm({ email: "", subject: "", message: "" });
    } catch (err) {
      console.error("Error submitting contact form:", err);
      posthog.capture('Contact Request Failed', {
        error: err.message,
        email: form.email,
      });
      toast.error(t("contactFail"));
    } finally {
      setLoading(false);
    }
  };

  return (
        <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t("meta.contact.title")}</title>
        <meta name="description" content={t("meta.contact.description")} />
          <link
          rel="alternate"
          hrefLang="en"
          href="https://www.insightsflows.com/ContactUsPage"
        />
        <link
          rel="alternate"
          hrefLang="ar"
          href="https://www.insightsflows.com/ContactUsPage"
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href="https://www.insightsflows.com/ContactUsPage"
        />
      </Helmet>

    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white p-4 relative">
      <Link
        to="/"
        className="absolute top-6 left-6 text-blue-600 hover:text-blue-800 transition"
        title="Back to Home"
      >
        <Home size={28} />
      </Link>
      <LanguageSwitcher />
      <motion.div
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-xl border-t-4 border-blue-500"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-3xl font-bold mb-4 text-center text-gray-800">
          {t("contactTitle")}
        </h2>
        <p className="text-gray-500 text-sm text-center mb-6">
          {t("contactSubtitle")}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block mb-1 font-medium text-gray-700">
              {t("contactEmail")}
            </label>
            <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-blue-300">
              <Mail className="w-5 h-5 text-gray-400 mr-2" />
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                placeholder={t("contactPlaceholderEmail")}
                className="w-full outline-none bg-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 font-medium text-gray-700">
              {t("contactSubject")}
            </label>
            <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-blue-300">
              <Tag className="w-5 h-5 text-gray-400 mr-2" />
              <input
                type="text"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                required
                placeholder={t("contactPlaceholderSubject")}
                className="w-full outline-none bg-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 font-medium text-gray-700">
              {t("contactMessage")}
            </label>
            <div className="flex items-start border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-blue-300">
              <MessageSquare className="w-5 h-5 text-gray-400 mr-2 mt-1" />
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={5}
                required
                placeholder={t("contactPlaceholderMessage")}
                className="w-full outline-none bg-transparent resize-none"
              />
            </div>
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            disabled={loading}
            className={`w-full py-3 px-6 rounded-lg text-white font-semibold transition-all duration-200 ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600"
            }`}
          >
            {loading ? t("contactSubmitting") : t("contactSubmit")}
          </motion.button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-500">
          {t("demoAlreadyHave")}{" "}
          <Link
            to="/login"
            className="text-blue-600 font-semibold hover:underline inline-flex items-center"
          >
            <LogIn className="w-4 h-4 mr-1" /> {t("demoLogin")}
          </Link>
        </div>
      </motion.div>
    </div>
        </>
  );
}
